import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { translate, type TranslationKey } from '@/i18n/dictionary'
import { DEFAULT_LOCALE, getActiveLocale, type Locale } from '@/i18n/locale'
import { getActiveCurrency } from '@/lib/currency'

/**
 * Merge Tailwind classes, later ones winning conflicts.
 *
 * Required by shadcn/ui — `shadcn init` will rewrite this file to contain only
 * this function, so anything else added here must be restored afterwards. It
 * already happened once.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Translate outside React, the same way `lib/api.ts` does.
 *
 * These formatters are called from render paths and from plain functions
 * alike, so a hook is not available. The locale comes from the module-level
 * value the axios interceptor already reads, which means a language switch
 * moves them on the next render without anything subscribing.
 */
const t = (key: TranslationKey, values?: Record<string, string | number>) =>
  translate(getActiveLocale(), key, values)

const tp = (stem: string, count: number) =>
  t(`${stem}.${pluralForm(count)}` as TranslationKey, { count })

/**
 * Number and date formatting locale.
 *
 * Derived from the interface language rather than fixed at `sr-RS`, which is
 * what it was — so a Croatian or English screen still got Serbian month names
 * out of `toLocaleDateString` and a decimal comma out of `toLocaleString`.
 *
 * en-GB rather than en-US on purpose: this is a European factory, and
 * `03/04/2026` meaning April to half the readers and March to the other half is
 * the worst possible outcome on a maintenance record.
 *
 * ## Bosnian does not format as `bs-BA`
 *
 * It should, and in Node it does — which is exactly why this went unnoticed.
 * **Chrome ships no usable CLDR data for Bosnian.** Measured on Chrome 148, the
 * default locale of this entire application rendered:
 *
 *     (1234.5).toLocaleString('bs-BA')  →  "1,234.5"      (English separators)
 *     date, 2-digit d/m/y               →  "2026-09-03"   (ISO, not dd.mm.yyyy.)
 *     month: 'long'                     →  "M09"          (a raw CLDR placeholder)
 *
 * So every amount of money, every duration and every due date on the Bosnian
 * interface was being shown to the factory in a foreign format, and the MES
 * badge was printing "M09" where a month should be. The test suite cannot see
 * any of it: vitest runs on Node, whose ICU has the Bosnian data Chrome lacks,
 * so an assertion that `formatMoney` yields a comma passes while the browser
 * renders a point.
 *
 * `sr-Latn-RS` is the formatting stand-in, and it is chosen on data rather than
 * on nationality: it produces `1.234,5`, `03.09.2026.` and `septembar` — which
 * is Bosnian convention exactly, down to the month name — and it does so
 * identically in Node and in Chrome. `hr-HR` was the other candidate and is
 * worse on both counts: `03. 09. 2026.` with spaces, and `rujan`, which is not
 * the Bosnian word.
 *
 * Serbian moves to the Latin variant for a plainer reason: this application's
 * Serbian is written in Latin script throughout, and `sr-RS` returns Cyrillic
 * month names.
 *
 * If Chrome ever ships real `bs` data, `bs-BA` becomes correct and this comment
 * becomes the reason it was ever anything else. Check with `month: 'long'` —
 * "M09" is the tell.
 */
export const INTL_LOCALE: Record<Locale, string> = {
  bs: 'sr-Latn-RS',
  hr: 'hr-HR',
  sr: 'sr-Latn-RS',
  en: 'en-GB',
}

const intl = () => INTL_LOCALE[getActiveLocale()]

export function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleDateString(intl(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—'
  return new Date(value).toLocaleString(intl(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Relative time in Serbian.
 *
 * Supervisors scan a list looking for what has been sitting too long, and
 * "prije 3 dana" reads faster than a timestamp they have to subtract from today.
 */
export function formatRelative(value: string | null | undefined): string {
  if (!value) return '—'

  const diffMs = Date.now() - new Date(value).getTime()
  const minutes = Math.round(diffMs / 60000)

  if (minutes < 1) return t('time.justNow')
  if (minutes < 60) return t('time.minutesAgo', { count: minutes })

  const hours = Math.round(minutes / 60)
  if (hours < 24) return t('time.hoursAgo', { count: hours })

  const days = Math.round(hours / 24)
  if (days === 1) return t('time.yesterday')
  if (days < 30) return tp('time.daysAgo', days)

  return formatDate(value)
}

/** Hours as something readable: 3,5 h or 2 dana. */
export function formatHours(hours: number | null | undefined): string {
  if (hours === null || hours === undefined) return '—'
  if (hours < 1) return t('unit.minutesShort', { count: Math.round(hours * 60) })

  if (hours < 48) {
    return t('unit.hoursShort', {
      count: hours.toLocaleString(intl(), { maximumFractionDigits: 1 }),
    })
  }

  /*
   * Rounded to whole days for the plural form, but shown with one decimal.
   * "2,5 dana" takes the same form as "2 dana", so the form is chosen from the
   * integer part and the formatted value substituted in.
   */
  const days = hours / 24

  return t(`unit.daysLong.${pluralForm(Math.round(days))}` as TranslationKey, {
    count: days.toLocaleString(intl(), { maximumFractionDigits: 1 }),
  })
}

/**
 * An amount, in the currency this customer counts in.
 *
 * The symbol used to be baked into the translation — `'{amount} KM'` — which
 * was one string to change per language and wrong for every customer outside
 * Bosnia. It is a parameter now, so the *sentence* stays translatable while the
 * unit comes from the company.
 *
 * The separators are the locale's and the symbol's placement is the currency's,
 * because they are different facts: `1.234,56` is how the reader writes
 * numbers, and `$` in front is how the dollar is written in any of them.
 */
export function formatMoney(value: number | string | null | undefined): string {
  return formatMoneyIn(value, getActiveCurrency())
}

/**
 * An amount in a currency that is not the reader's own.
 *
 * The owner console shows every customer's savings, and the active currency
 * there is the platform's — a customer counting in euros would otherwise have
 * their savings printed in marks. Same rules as `formatMoney`, with the
 * currency passed in.
 */
export function formatMoneyIn(
  value: number | string | null | undefined,
  currency: { symbol: string; symbolLeads?: boolean },
): string {
  if (value === null || value === undefined || value === '') return '—'

  const amount = Number(value).toLocaleString(intl(), { maximumFractionDigits: 2 })

  return currency.symbolLeads
    ? `${currency.symbol}${amount}`
    : t('unit.money', { amount, currency: currency.symbol })
}

/**
 * Just the symbol, for a form label or a column heading.
 *
 * A field asking for an amount says which currency in its label — "Procjena
 * uštede (KM)" — rather than repeating the symbol inside the input, and a table
 * whose whole column is money says it once at the top.
 */
export function currencySymbol(): string {
  return getActiveCurrency().symbol
}

export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return value.toLocaleString(intl())
}

export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'
  return `${value.toLocaleString(intl(), { maximumFractionDigits: 1 })}%`
}

/**
 * Which of the three plural forms a count takes.
 *
 *   1 ocjena · 2 ocjene · 5 ocjena · 21 ocjena · 22 ocjene · 25 ocjena
 *
 * The rule keys on the last two digits, not on the number itself — 21 behaves
 * like 1 and 11 does not, which is why `count === 1` is not good enough and why
 * the naive `count < 5` check goes wrong from 21 upward.
 *
 * Shared by all three local standards. **English is not one of them**, and the
 * `locale` argument is why: its rule is simply one-or-not-one, so applying the
 * Slavic one to it produced "21 day" and "21 machine" — the last digit is a 1,
 * so `one` won. That was wrong everywhere English had a plural, not just where
 * it was noticed, and it was hidden by the fact that most counts on a shop
 * floor are under twenty.
 *
 * English still fills all three slots: `few` and `many` carry the same plural
 * sentence, and `one` the singular. Nothing in the dictionaries changes.
 *
 * The API has the same rule in App\Support\SerbianPlural. That one cannot be
 * shared — it is a different runtime — but this one must not be forked *within*
 * the client, which is why the dictionary's `tp()` reads it from here rather
 * than carrying its own copy.
 */
export function pluralForm(count: number, locale: Locale = DEFAULT_LOCALE): 'one' | 'few' | 'many' {
  // One or not one. `few` is never asked for in English, and `many` carries the
  // same sentence as it would, so the two-form language needs no third slot.
  if (locale === 'en') {
    return Math.abs(count) === 1 ? 'one' : 'many'
  }

  const lastTwo = Math.abs(count) % 100
  const last = lastTwo % 10

  // 11–14 are the exception: they take the "many" form despite their last digit.
  if (lastTwo >= 11 && lastTwo <= 14) return 'many'
  if (last === 1) return 'one'
  if (last >= 2 && last <= 4) return 'few'

  return 'many'
}

/**
 * The right one of three literal forms.
 *
 * Predates the dictionary and survives for the handful of places composing a
 * string from values that are not translatable text. Anything with a
 * translatable noun in it should use `tp()` instead, which picks the form *and*
 * the language.
 */
export function plural(count: number, one: string, few: string, many: string): string {
  return { one, few, many }[pluralForm(count)]
}

/**
 * Tailwind classes for a badge, keyed by the colour name the API sends.
 *
 * The server decides which colour a status or priority is, so the meaning stays
 * in one place; this map only turns that name into classes.
 */
const BADGE_COLORS: Record<string, string> = {
  slate: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  sky: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-300',
  indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
  amber: 'bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300',
  orange: 'bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-300',
  emerald: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  teal: 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300',
  rose: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
}

export function badgeColor(color: string | null | undefined): string {
  return BADGE_COLORS[color ?? 'slate'] ?? BADGE_COLORS.slate
}

/** Chart series colours, in a fixed order so a series keeps its colour. */
export const CHART_COLORS = [
  'oklch(0.58 0.18 250)',
  'oklch(0.65 0.16 160)',
  'oklch(0.72 0.16 70)',
  'oklch(0.62 0.2 20)',
  'oklch(0.6 0.16 300)',
  'oklch(0.68 0.13 200)',
  'oklch(0.55 0.14 130)',
]
